import { brand } from '@alison/ui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatMetricValue } from '../../data/format.js';
import { MetricDelta } from '../../MetricDelta.js';
import type { BenchmarkedMetric } from '../data/api.js';

const COMPARISONS = [
  { key: 'previous', suffix: 'vs. prev period' },
  { key: 'account', suffix: 'vs. account avg' },
] as const;

const SPEND_METRIC = 'cost';

interface BenchmarkedMetricTileProps {
  metric: BenchmarkedMetric;
  currencySymbol: string;
  selected?: boolean;
  onSelect?: (metricName: string) => void;
}

export function BenchmarkedMetricTile({
  metric,
  currencySymbol,
  selected = false,
  onSelect,
}: BenchmarkedMetricTileProps): ReactNode {
  return (
    <Box
      {...(onSelect === undefined
        ? {}
        : { component: 'button', type: 'button', onClick: () => onSelect(metric.name) })}
      aria-pressed={onSelect === undefined ? undefined : selected}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        p: 2,
        m: 0,
        font: 'inherit',
        textAlign: 'left',
        color: 'inherit',
        border: '1px solid',
        borderColor: selected ? theme.palette.border.light : 'transparent',
        borderRadius: `${theme.radius.md}px`,
        bgcolor: selected ? brand.bgBlue : 'transparent',
        cursor: onSelect === undefined ? 'default' : 'pointer',
        transition: theme.transitions.create(['background-color', 'border-color'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': selected || onSelect === undefined ? {} : { bgcolor: 'background.page' },
      })}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {metric.label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 500, color: 'text.primary', lineHeight: 1.2 }}>
        {formatMetricValue(metric.value, metric.format, currencySymbol)}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {metric.name === SPEND_METRIC ? (
          <SpendRatio spend={metric.value} accountSpend={metric.account.value} />
        ) : (
          COMPARISONS.map(({ key, suffix }) =>
            metric[key].delta === null ? null : (
              <MetricDelta
                key={key}
                delta={metric[key].delta}
                sentiment={metric[key].sentiment}
                suffix={suffix}
              />
            ),
          )
        )}
      </Box>
    </Box>
  );
}

function SpendRatio({
  spend,
  accountSpend,
}: {
  spend: number | null;
  accountSpend: number | null;
}): ReactNode {
  if (spend === null || accountSpend === null || accountSpend === 0) return null;
  return (
    <Typography
      component="span"
      sx={{ fontSize: 10, fontWeight: 700, px: 1, color: 'text.secondary', whiteSpace: 'nowrap' }}
    >
      {formatMetricValue(spend / accountSpend, 'percentage', '')} spend ratio
    </Typography>
  );
}
